import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, Typography, useTheme, InputBase, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem
} from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import SearchIcon from "@mui/icons-material/Search";
import AddCardIcon from "@mui/icons-material/AddCard";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

const Payments = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");

  // Modal manuel
  const [openModal, setOpenModal] = useState(false);
  const [manualPayment, setManualPayment] = useState({
    driver_id: "",
    months: 1,
    amount: 3000,
    payment_method: "cash"
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, page, search, limit: 20 }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch payments");
      }

      const data = await response.json();
      setPayments(data.data || []);
      setRowCount(data.total || (data.data ? data.data.length : 0));
    } catch (error) {
      console.error("Error fetching payments:", error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleManualPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!manualPayment.driver_id) {
      toast.error("Veuillez saisir l'ID du chauffeur.");
      return;
    }
    setSubmitting(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/validate-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authToken,
          driver_id: manualPayment.driver_id,
          months: parseInt(manualPayment.months),
          amount: parseFloat(manualPayment.amount),
          payment_method: manualPayment.payment_method
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || "Paiement validé et abonnement prolongé !");
        setOpenModal(false);
        setManualPayment({ driver_id: "", months: 1, amount: 3000, payment_method: "cash" });
        fetchPayments();
      } else {
        toast.error(data.message || "Erreur de validation");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur serveur");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { field: "id", headerName: "Réf Paiement", flex: 0.9 },
    {
      field: "driver_name",
      headerName: "Chauffeur",
      flex: 1.2,
      renderCell: (params) => (
        <Box>
          <Typography fontWeight="bold" color={colors.grey[100]}>
            {params.value || params.row.driver_id}
          </Typography>
          {params.row.driver_phone && (
            <Typography variant="caption" color={colors.grey[300]}>
              {params.row.driver_phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "total_amount",
      headerName: "Montant",
      flex: 0.8,
      renderCell: (params) => (
        <Typography fontWeight="bold" color={colors.greenAccent[500]}>
          {params.value || params.row.amount} DZD
        </Typography>
      ),
    },
    { field: "type", headerName: "Type", flex: 0.8 },
    { field: "payment_method", headerName: "Méthode", flex: 0.8 },
    {
      field: "status",
      headerName: "Statut",
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          label={params.value || "completed"}
          size="small"
          sx={{
            backgroundColor: params.value === "completed" ? colors.greenAccent[600] : colors.blueAccent[600],
            color: "#fff"
          }}
        />
      ),
    },
    { field: "reference", headerName: "N° Transaction", flex: 1.1 },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[200]}>
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
          }) : "-"}
        </Typography>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="10px">
        <Header title="PAIEMENTS & TRANSACTIONS" subtitle="Suivi des abonnements chauffeurs et paiements en ligne" />
        <Button
          onClick={() => setOpenModal(true)}
          variant="contained"
          startIcon={<AddCardIcon />}
          sx={{
            backgroundColor: colors.greenAccent[600],
            color: "#fff",
            fontWeight: "bold",
            padding: "10px 20px",
            "&:hover": { backgroundColor: colors.greenAccent[700] }
          }}
        >
          Valider Paiement Manuel
        </Button>
      </Box>

      <Box display="flex" backgroundColor={colors.primary[400]} borderRadius="3px" mt="15px">
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ml: 2, flex: 1 }}
          placeholder="Rechercher par chauffeur, téléphone, référence..."
        />
        <IconButton type="button" sx={{ p: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <Box
        m="20px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
        }}
      >
        <DataGrid
          rows={payments}
          columns={columns}
          slots={{ toolbar: CustomToolbar }}
          pagination
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          onPaginationModelChange={(newModel) => setPage(newModel.page + 1)}
        />
      </Box>

      {/* MODAL AJOUT PAIEMENT MANUEL */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleManualPaymentSubmit}>
          <DialogTitle sx={{ backgroundColor: colors.primary[400], color: colors.grey[100] }}>
            Enregistrer un Paiement & Prolonger l'Abonnement
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: colors.primary[400], pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="ID Chauffeur ou Téléphone"
              fullWidth
              required
              value={manualPayment.driver_id}
              onChange={(e) => setManualPayment({ ...manualPayment, driver_id: e.target.value })}
              placeholder="Ex: driver-12345"
              sx={{ mt: 1 }}
            />
            <TextField
              select
              label="Durée de l'Abonnement"
              fullWidth
              value={manualPayment.months}
              onChange={(e) => setManualPayment({ ...manualPayment, months: e.target.value, amount: e.target.value * 3000 })}
            >
              <MenuItem value={1}>1 Mois (3 000 DZD)</MenuItem>
              <MenuItem value={3}>3 Mois (8 000 DZD)</MenuItem>
              <MenuItem value={6}>6 Mois (15 000 DZD)</MenuItem>
              <MenuItem value={12}>1 An (28 000 DZD)</MenuItem>
            </TextField>
            <TextField
              label="Montant Encaissé (DZD)"
              type="number"
              fullWidth
              value={manualPayment.amount}
              onChange={(e) => setManualPayment({ ...manualPayment, amount: e.target.value })}
            />
            <TextField
              select
              label="Mode de Règlement"
              fullWidth
              value={manualPayment.payment_method}
              onChange={(e) => setManualPayment({ ...manualPayment, payment_method: e.target.value })}
            >
              <MenuItem value="cash">Espèces (Au bureau)</MenuItem>
              <MenuItem value="virement">Virement Bancaire / CCP</MenuItem>
              <MenuItem value="chargily">Chargily (Edahabia / CIB)</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: colors.primary[400], p: 2 }}>
            <Button onClick={() => setOpenModal(false)} color="inherit">
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={{ backgroundColor: colors.greenAccent[600] }}>
              {submitting ? "Validation..." : "Valider et Activer"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Payments;
